import type { ApiEndpoints, ProjectSchema } from '@avisoma//shared';
import { FaFile } from 'react-icons/fa6';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { Badge } from '../../catalyst/badge';
import { Link } from '../../components/Link';
import { ID } from '../../components/ID';
import CopyButton from '../../components/CopyButton';
import AuthGuard from '../../components/AuthGuard';
import { formatDateTime, formatRelativeTime } from '../../lib/dateFormat';
import { listUndeliveredFamilies, UndeliveredFamilyList } from '../../components/undeliveredFamilies';

interface ProjectDetailsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

export function ProjectDetails({ response }: ProjectDetailsProps) {
  const data: ProjectSchema['data'] = response.item.data;
  // A finalized project can still owe Oil rollup families (DR-2855) — surface
  // the debt next to the green Status badge rather than letting it hide.
  const hasUndelivered = listUndeliveredFamilies(response.item.undeliveredFamilies).length > 0;

  return (
    <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Project Information</h2>}>
      <DescriptionList>
        <DescriptionTerm>Project ID</DescriptionTerm>
        <DescriptionDetails className="flex items-center gap-2">
          {response.item.id}
          <CopyButton text={response.item.id} />
        </DescriptionDetails>
        <DescriptionTerm>Created At</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(response.item.createdAt)}</DescriptionDetails>
        <DescriptionTerm>Updated At</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(response.item.updatedAt)}</DescriptionDetails>
        <DescriptionTerm>Status</DescriptionTerm>
        <DescriptionDetails>
          {response.item.isFinalized ? (
            <span className="inline-flex items-center gap-2">
              <Badge color="emerald">Finalized</Badge>
              {data?.finalizedAt && (
                <span
                  className="text-xs text-zinc-500 dark:text-zinc-400"
                  title={formatDateTime(data.finalizedAt)}
                >
                  {formatRelativeTime(data.finalizedAt)}
                </span>
              )}
            </span>
          ) : response.item.finalizeError ? (
            <span className="inline-flex items-center gap-2">
              <Badge color="red">Finalization failed</Badge>
              <span
                className="text-xs text-zinc-500 dark:text-zinc-400"
                title={formatDateTime(response.item.finalizeError.occurredAt)}
              >
                {formatRelativeTime(response.item.finalizeError.occurredAt)}
              </span>
            </span>
          ) : (
            <Badge color="zinc">In progress</Badge>
          )}
        </DescriptionDetails>
        {!response.item.isFinalized && response.item.finalizeError && (
          <>
            <DescriptionTerm>Finalization error</DescriptionTerm>
            <DescriptionDetails className="text-red-700 dark:text-red-400">
              {response.item.finalizeError.message}
            </DescriptionDetails>
          </>
        )}
        {hasUndelivered && (
          <>
            <DescriptionTerm>Undelivered to Oil</DescriptionTerm>
            <DescriptionDetails>
              <UndeliveredFamilyList families={response.item.undeliveredFamilies} />
            </DescriptionDetails>
          </>
        )}
        <DescriptionTerm>ESN</DescriptionTerm>
        <DescriptionDetails>{data?.projectData?.esn ?? '—'}</DescriptionDetails>
        <DescriptionTerm>Engine Type</DescriptionTerm>
        <DescriptionDetails>{data?.projectData?.engineType ?? '—'}</DescriptionDetails>
        <DescriptionTerm>Total pages</DescriptionTerm>
        <DescriptionDetails>{response.item.modelUsage.totalPageCount}</DescriptionDetails>
        {response.item.organisation && (
          <>
            <DescriptionTerm>Organisation</DescriptionTerm>
            <DescriptionDetails>
              <Link
                to={`/organisations/${encodeURIComponent(response.item.organisation.id)}`}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {response.item.organisation.name}
              </Link>
            </DescriptionDetails>
          </>
        )}
        <DescriptionTerm>Uploads</DescriptionTerm>
        <DescriptionDetails>
          <div className="flex flex-wrap gap-1 max-h-[400px] overflow-y-auto">
            {response.uploads.map((upload) => (
              <AuthGuard
                key={upload.id}
                roles={['admin']}
                fallback={
                  <span
                    title={upload.id}
                    className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300"
                  >
                    <FaFile className="size-3" />
                    <ID id={upload.id} />
                  </span>
                }
              >
                <Link
                  to={`/uploads/${upload.id}`}
                  title={upload.id}
                  className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300 dark:hover:bg-indigo-900/40"
                >
                  <FaFile className="size-3" />
                  <ID id={upload.id} />
                </Link>
              </AuthGuard>
            ))}
          </div>
        </DescriptionDetails>

        <DescriptionTerm>Webhook URL</DescriptionTerm>
        <DescriptionDetails>{response.item.webhookUrl ?? '—'}</DescriptionDetails>
        <DescriptionTerm>Topic</DescriptionTerm>
        <DescriptionDetails>{response.item.topic ?? '—'}</DescriptionDetails>
      </DescriptionList>
    </Card>
  );
}
